import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import type { TranscriptKind, TranscriptSummary } from '../../../shared/api.js';
import { api } from '../lib/api';
import { formatDateTime, formatShortDateTime } from '../lib/format';
import { RecordingsPanel } from './RecordingsPanel';

/** Tab names in tab order; every kind must be here (the type checks it). */
const KIND_LABELS: Record<TranscriptKind, string> = {
  voice_note: 'Голосовые заметки',
  call: 'Звонки',
  other: 'Другое',
};
const KINDS = Object.keys(KIND_LABELS) as TranscriptKind[];

function formatLength(sec: number | null): string | undefined {
  if (sec === null) return undefined;
  if (sec < 60) return `${sec} с`;
  const minutes = Math.round(sec / 60);
  return minutes < 60 ? `${minutes} мин` : `${Math.floor(minutes / 60)} ч ${minutes % 60} мин`;
}

function author(label: string | null): string | undefined {
  return label?.replace(/^(user|local):/, '');
}

function meta(t: TranscriptSummary): string {
  return [
    formatDateTime(t.recordedAt),
    formatLength(t.durationSec),
    t.participants.length > 0 ? t.participants.join(', ') : undefined,
    t.source ?? undefined,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The whole transcript, opened from the list. */
function TranscriptCard({
  id,
  canWrite,
  onClose,
}: {
  id: number;
  canWrite: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['transcripts', id], queryFn: () => api.transcript(id) });
  const remove = useMutation({
    mutationFn: () => api.deleteTranscript(id),
    onSuccess: () => {
      onClose();
      void queryClient.invalidateQueries({ queryKey: ['transcripts'] });
    },
  });
  const t = query.data?.transcript;
  return (
    <article className="card transcript">
      <div className="card__header">
        <h3>{t?.title ?? 'Расшифровка'}</h3>
        <div className="actions-row">
          {canWrite && t && (
            <button
              type="button"
              className="btn btn--small btn--ghost"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Удалить «${t.title}»? Это навсегда и для всех.`)) {
                  remove.mutate();
                }
              }}
            >
              Удалить
            </button>
          )}
          <button type="button" className="btn btn--small btn--ghost" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {remove.isError && <p className="text-error small">{remove.error.message}</p>}
      {t && (
        <>
          <p className="muted small">
            {KIND_LABELS[t.kind]} · {meta(t)}
            {t.language && ` · ${t.language}`}
            {author(t.createdBy) && ` · добавил ${author(t.createdBy)}`}
            {` · ${formatShortDateTime(t.createdAt)}`}
          </p>
          {t.summary && (
            <p className="transcript__summary">
              <strong>Кратко.</strong> {t.summary}
            </p>
          )}
          <pre className="transcript__text">{t.text}</pre>
        </>
      )}
    </article>
  );
}

/** Pasting a transcript by hand; the agent does the same with add_transcript. */
function AddTranscriptForm({ onAdded }: { onAdded: (id: number) => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<TranscriptKind>('voice_note');
  const [title, setTitle] = useState('');
  const [recordedAt, setRecordedAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [participants, setParticipants] = useState('');
  const [source, setSource] = useState('');
  const [summary, setSummary] = useState('');
  const [text, setText] = useState('');
  const add = useMutation({
    mutationFn: async () => {
      const response = await api.addTranscript({
        kind,
        title: title.trim(),
        recordedAt,
        participants: participants
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean),
        ...(source.trim() ? { source: source.trim() } : {}),
        ...(summary.trim() ? { summary: summary.trim() } : {}),
        text: text.trim(),
      });
      return (await response.json()) as { transcript: { id: number } };
    },
    onSuccess: (result) => {
      setTitle('');
      setSummary('');
      setText('');
      void queryClient.invalidateQueries({ queryKey: ['transcripts'] });
      onAdded(result.transcript.id);
    },
  });
  const ready = title.trim().length > 0 && text.trim().length > 0 && recordedAt.length > 0;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) add.mutate();
  };
  return (
    <form className="card form form--wide" onSubmit={submit}>
      <div className="card__header">
        <h3>Добавить расшифровку</h3>
      </div>
      <label>
        Название
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          required
          placeholder="Созвон с Анной про октябрь"
        />
      </label>
      <div className="form__row">
        <label>
          Вид
          <select value={kind} onChange={(e) => setKind(e.target.value as TranscriptKind)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Когда записано
          <input
            type="datetime-local"
            value={recordedAt}
            onChange={(e) => setRecordedAt(e.target.value)}
            required
          />
        </label>
      </div>
      <div className="form__row">
        <label>
          Участники <span className="muted small">(через запятую)</span>
          <input
            value={participants}
            onChange={(e) => setParticipants(e.target.value)}
            placeholder="Геннадий, Анна"
          />
        </label>
        <label>
          Источник
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            maxLength={100}
            placeholder="Zoom, Telegram, диктофон"
          />
        </label>
      </div>
      <label>
        Кратко <span className="muted small">(необязательно)</span>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={2}
          maxLength={4000}
        />
      </label>
      <label>
        Текст расшифровки
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} required />
      </label>
      {add.isError && <p className="text-error small">{add.error.message}</p>}
      <div className="actions-row">
        <button type="submit" className="btn btn--primary" disabled={!ready || add.isPending}>
          {add.isPending ? 'Сохраняю…' : 'Сохранить'}
        </button>
      </div>
    </form>
  );
}

/**
 * Transcripts of voice notes and calls: newest first, by kind, with a word search. Everyone
 * reads; adding and deleting need the right `transcripts:write` (the owner gives it in
 * Settings → Users) or the owner role.
 */
export function Transcripts() {
  const [kind, setKind] = useState<TranscriptKind | undefined>();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<number | undefined>();
  const [adding, setAdding] = useState(false);
  const list = useQuery({
    queryKey: ['transcripts', 'list', kind ?? '', query],
    queryFn: () =>
      api.transcripts({ ...(kind ? { kind } : {}), ...(query ? { query } : {}), limit: 100 }),
  });
  const items = list.data?.transcripts ?? [];
  const canWrite = list.data?.canWrite ?? false;

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setQuery(search.trim());
  };

  return (
    <div className="page">
      <div className="page__header">
        <h2>Расшифровки</h2>
        <div className="actions-row">
          <form className="search" onSubmit={submitSearch}>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по словам"
              aria-label="Поиск по расшифровкам"
            />
            <button type="submit" className="btn btn--small">
              Найти
            </button>
          </form>
          {canWrite && (
            <button
              type="button"
              className="btn btn--small btn--primary"
              onClick={() => setAdding((v) => !v)}
            >
              {adding ? 'Скрыть форму' : 'Добавить'}
            </button>
          )}
        </div>
      </div>
      <p className="muted">
        Голосовые заметки и звонки, которые сохранили агент или пользователи. Читают все; добавлять
        и удалять могут владелец и те, кому он выдал право в Настройках → Пользователи.
      </p>
      {adding && canWrite && (
        <AddTranscriptForm
          onAdded={(id) => {
            setAdding(false);
            setOpen(id);
          }}
        />
      )}
      <RecordingsPanel onOpen={setOpen} />
      <div className="tabs" role="tablist" aria-label="Виды расшифровок">
        <button
          type="button"
          role="tab"
          aria-selected={kind === undefined}
          className={kind === undefined ? 'active' : undefined}
          onClick={() => setKind(undefined)}
        >
          Все
        </button>
        {KINDS.map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={k === kind}
            className={k === kind ? 'active' : undefined}
            onClick={() => setKind(k)}
          >
            {KIND_LABELS[k]}
          </button>
        ))}
      </div>
      {open !== undefined && (
        <TranscriptCard id={open} canWrite={canWrite} onClose={() => setOpen(undefined)} />
      )}
      {list.isPending && <p className="muted">Загрузка…</p>}
      {list.isError && <p className="text-error">{list.error.message}</p>}
      {list.data && !list.data.available && (
        <p className="muted">Расшифровки недоступны: нет базы.</p>
      )}
      {list.data?.available && items.length === 0 && (
        <p className="muted">
          {query
            ? 'Ничего не найдено.'
            : 'Расшифровок пока нет. Попросите агента: «сохрани расшифровку этого звонка».'}
        </p>
      )}
      {items.length > 0 && (
        <ul className="card page-list">
          {items.map((t) => (
            <li key={t.id} className="page-row">
              <div className="page-row__main">
                <button
                  type="button"
                  className="link-button page-row__title"
                  onClick={() => setOpen(t.id)}
                >
                  {t.title}
                </button>
                <span className="muted small">
                  {KIND_LABELS[t.kind]} · {meta(t)}
                </span>
                <span className="small transcript__excerpt">
                  {t.snippet ?? t.summary ?? t.excerpt}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {list.data && list.data.total > items.length && (
        <p className="muted small">
          Показаны {items.length} из {list.data.total}; уточните поиск.
        </p>
      )}
    </div>
  );
}
